import { useEffect, useState } from "react";
import { isUserOnTV } from "@sinclair-digital/cabin-auth-library";
import { CustomPiecesConstants } from "../../../utils/constants";

interface FallingKingProps {
    squareWidth: number;
    isWhite: boolean | undefined;
    calculateLeftOffset: (squareWidth: number, isDragging: boolean) => string;
    calculateBottomOffset: (squareWidth: number) => string;
    piece: string;
}

const FallingKing = ({
    squareWidth,
    isWhite,
    calculateLeftOffset,
    calculateBottomOffset,
    piece,
}: FallingKingProps) => {
    const [kingFrameIndex, setKingFrameIndex] = useState(0);
    const [animationComplete, setAnimationComplete] = useState(false);

    useEffect(() => {
        let frame = 0;
        const isTV = isUserOnTV();
        const frameIncrement = isTV ? 2 : 1;
        const totalFrames = isTV ? 26 : 52;
        const frameRate = 1000 / 30;

        const interval = setInterval(() => {
            setKingFrameIndex(frame);
            frame += frameIncrement;

            if (frame / frameIncrement >= totalFrames) {
                clearInterval(interval);
                // Show normal king after animation completes
                setTimeout(() => setAnimationComplete(true), 1000);
            }
        }, frameRate);

        return () => clearInterval(interval);
    }, []);

    // If animation is complete, show the normal king piece with shadow
    if (animationComplete) {
        return (
            <>
                <img
                    src={`images/${piece}.webp`}
                    className="chess-piece-image"
                    width={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                    height={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                    alt="King"
                    style={{
                        left: calculateLeftOffset(squareWidth, false),
                        bottom: calculateBottomOffset(squareWidth),
                        objectFit: piece[1] === "K" ? "contain" : "cover", // Match the original logic
                    }}
                />
                <img
                    src={`images/${piece}.webp`}
                    className="chess-piece-shadow"
                    width={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                    height={squareWidth * CustomPiecesConstants.ZOOM_FACTOR}
                    alt="King Shadow"
                    style={{
                        left: calculateLeftOffset(squareWidth, false),
                        bottom: calculateBottomOffset(squareWidth),
                        marginBottom: `${squareWidth * 0.3}px`, // Match the original shadow offset
                        objectFit: piece[1] === "K" ? "contain" : "cover",
                    }}
                />
            </>
        );
    }

    const paddedIndex = String(kingFrameIndex + 1).padStart(2, "0");
    const scaleX = 1.25;
    const scaleY = 1.25;

    // Otherwise show the falling animation
    return (
        <>
            <img
                src={`/images/kingFall/king-${isWhite ? "white" : "black"}-${paddedIndex}.webp`}
                className="chess-piece-image"
                width={squareWidth * CustomPiecesConstants.ZOOM_FACTOR * scaleX}
                height={squareWidth * CustomPiecesConstants.ZOOM_FACTOR * scaleY}
                alt="Falling King"
                style={{
                    left: `calc(${calculateLeftOffset(squareWidth, false)} + ${squareWidth * 0.175}px)`,
                    bottom: `calc(${calculateBottomOffset(squareWidth)} - ${squareWidth * 0.06}px)`,
                    objectFit: "contain",
                    zIndex: "10 !important",
                }}
            />
            <img
                src={`/images/kingFall/king-shadow-${paddedIndex}.webp`}
                className="chess-piece-shadow"
                width={squareWidth * CustomPiecesConstants.ZOOM_FACTOR * scaleX}
                height={squareWidth * CustomPiecesConstants.ZOOM_FACTOR * scaleY}
                alt="Falling King Shadow"
                style={{
                    left: `calc(${calculateLeftOffset(squareWidth, false)} + ${squareWidth * 0.175}px)`,
                    bottom: `calc(${calculateBottomOffset(squareWidth)} - ${squareWidth * 0.06}px)`,
                    marginBottom: `${squareWidth * 0.3}px`,
                    objectFit: "contain",
                    zIndex: "10 !important",
                }}
            />
        </>
    );
};

export default FallingKing;
